/**
 * Riley's chat text with every ticket (#23833) and case (C-00388) reference made clickable (issue #98): the same
 * ticket menu as everywhere else (copy, open case, open in Zoho Desk), and case refs as real links. A ticket ref
 * links to its case when Patchbay knows one, resolved in one batch through GET /api/v1/refs (side-effect free).
 * A failed lookup leaves the refs as plain badges (copy still works); it never hides the text.
 */
import { ErrorBanner } from '@patchbay/ui'
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query'
import type { Element, Root, RootContent, Text } from 'hast'
import * as React from 'react'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import { unified } from 'unified'
import { describeError, unwrap } from '../lib/api/client'
import { type RefsOut, refsApiV1RefsGet } from '../lib/api/generated'
import { CaseRefLink, TicketBadge } from './ticket-ref'

const REF = /(#\d{5,6})(?!\d)|\b([Cc]-\d{1,6})(?!\d)/g

export type RileyTextPart =
  | { kind: 'text'; text: string }
  | { kind: 'ticket'; number: string }
  | { kind: 'case'; text: string; number: string }

/** C-388 / c-00388 → C-00388 (the API's case_number format). */
export function normalCase(ref: string): string {
  const digits = ref.replace(/\D/g, '').replace(/^0+(?=\d)/, '')
  return `C-${digits.padStart(5, '0')}`
}

export function splitRefs(text: string): RileyTextPart[] {
  const out: RileyTextPart[] = []
  let last = 0
  for (const m of text.matchAll(REF)) {
    const at = m.index ?? 0
    if (at > last) out.push({ kind: 'text', text: text.slice(last, at) })
    if (m[1]) out.push({ kind: 'ticket', number: m[1].slice(1) })
    else if (m[2]) out.push({ kind: 'case', text: m[2], number: normalCase(m[2]) })
    last = at + m[0].length
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) })
  return out
}

// A badge rendered outside a provider (kit stories, some tests) still renders; it just never looks anything up.
const detached = new QueryClient({ defaultOptions: { queries: { retry: false } } })

export const refsQueryKey = (tickets: string[], cases: string[]) => ['refs', tickets.join(','), cases.join(',')] as const

function useRefs(tickets: string[], cases: string[]) {
  const qc = React.useContext(QueryClientContext)
  return useQuery(
    {
      queryKey: refsQueryKey(tickets, cases),
      queryFn: ({ signal }): Promise<RefsOut> =>
        unwrap(
          refsApiV1RefsGet({
            query: { tickets: tickets.join(',') || undefined, cases: cases.join(',') || undefined },
            signal,
          }),
        ),
      enabled: Boolean(qc) && (tickets.length > 0 || cases.length > 0),
      staleTime: 5 * 60_000,
      retry: 1,
    },
    qc ?? detached,
  )
}

const markdown = unified().use(remarkParse).use(remarkGfm).use(remarkRehype).use(rehypeSanitize, defaultSchema)

function markdownTree(text: string): Root {
  return markdown.runSync(markdown.parse(text)) as Root
}

function textValues(node: Root | RootContent): string[] {
  if (node.type === 'text') return [node.value]
  if ('children' in node) return node.children.flatMap((child) => textValues(child as RootContent))
  return []
}

type RefMaps = {
  tickets: Map<string, NonNullable<RefsOut['tickets']>[number]>
  cases: Map<string, NonNullable<RefsOut['cases']>[number]>
}

function refText(value: string, refs: RefMaps, key: string): React.ReactNode[] {
  return splitRefs(value).map((part, index) => {
    const partKey = `${key}:${index}`
    if (part.kind === 'text') return <React.Fragment key={partKey}>{part.text}</React.Fragment>
    if (part.kind === 'ticket') {
      const ticket = refs.tickets.get(part.number)
      return (
        <span key={partKey} className="mx-0.5 inline-flex align-baseline">
          <TicketBadge number={part.number} caseNumber={ticket?.case_number ?? undefined} deskTicketId={ticket?.desk_ticket_id} />
        </span>
      )
    }
    const kase = refs.cases.get(part.number)
    return (
      <CaseRefLink key={partKey} caseNumber={kase?.merged_into ?? part.number} deskTicketId={kase?.desk_ticket_id} className="font-mono">
        {part.text}
      </CaseRefLink>
    )
  })
}

function elementChildren(node: Element, refs: RefMaps, path: string, literal = false): React.ReactNode[] {
  return node.children.map((child, index) => renderMarkdownNode(child, refs, `${path}.${index}`, literal))
}

/** Render only the sanitised HAST vocabulary; no raw HTML or dangerouslySetInnerHTML. */
function renderMarkdownNode(node: RootContent, refs: RefMaps, path: string, literal = false): React.ReactNode {
  if (node.type === 'text') return literal ? (node as Text).value : refText((node as Text).value, refs, path)
  if (node.type !== 'element') return null
  const children = elementChildren(node, refs, path, literal || node.tagName === 'code' || node.tagName === 'pre')
  switch (node.tagName) {
    case 'p':
      return (
        <p key={path} className="whitespace-pre-wrap [overflow-wrap:anywhere]">
          {children}
        </p>
      )
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6': {
      const Heading = node.tagName
      return (
        <Heading key={path} className="pt-1 font-semibold leading-snug text-foreground">
          {children}
        </Heading>
      )
    }
    case 'ul':
      return (
        <ul key={path} className="list-disc space-y-1 pl-5">
          {children}
        </ul>
      )
    case 'ol':
      return (
        <ol key={path} className="list-decimal space-y-1 pl-5">
          {children}
        </ol>
      )
    case 'li':
      return <li key={path}>{children}</li>
    case 'strong':
      return <strong key={path}>{children}</strong>
    case 'em':
      return <em key={path}>{children}</em>
    case 'del':
      return <del key={path}>{children}</del>
    case 'blockquote':
      return (
        <blockquote key={path} className="border-l-2 border-riley-border pl-3 text-muted-foreground">
          {children}
        </blockquote>
      )
    case 'pre':
      return (
        <pre key={path} className="max-w-full overflow-x-auto rounded-md bg-muted px-2.5 py-2 font-mono text-xs whitespace-pre">
          {children}
        </pre>
      )
    case 'code':
      return (
        <code key={path} className="rounded bg-muted px-1 font-mono text-[0.85em]">
          {children}
        </code>
      )
    case 'a': {
      const href = typeof node.properties.href === 'string' ? node.properties.href : undefined
      return (
        <a key={path} href={href} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2">
          {elementChildren(node, refs, path, true)}
        </a>
      )
    }
    case 'table':
      return (
        <div key={path} className="max-w-full overflow-x-auto">
          <table className="w-max min-w-full border-collapse text-sm">{children}</table>
        </div>
      )
    case 'thead':
      return <thead key={path}>{children}</thead>
    case 'tbody':
      return <tbody key={path}>{children}</tbody>
    case 'tr':
      return <tr key={path}>{children}</tr>
    case 'th':
      return (
        <th key={path} className="border border-border px-2 py-1 text-left font-semibold">
          {children}
        </th>
      )
    case 'td':
      return (
        <td key={path} className="border border-border px-2 py-1 align-top">
          {children}
        </td>
      )
    case 'br':
      return <br key={path} />
    case 'hr':
      return <hr key={path} className="border-border" />
    default:
      return <React.Fragment key={path}>{children}</React.Fragment>
  }
}

export function RileyText({ text, className }: { text: string; className?: string }) {
  const tree = React.useMemo(() => markdownTree(text), [text])
  const parts = React.useMemo(() => textValues(tree).flatMap(splitRefs), [tree])
  const tickets = React.useMemo(() => [...new Set(parts.flatMap((p) => (p.kind === 'ticket' ? [p.number] : [])))].sort(), [parts])
  const cases = React.useMemo(() => [...new Set(parts.flatMap((p) => (p.kind === 'case' ? [p.number] : [])))].sort(), [parts])
  const refs = useRefs(tickets, cases)
  const maps: RefMaps = {
    tickets: new Map((refs.data?.tickets ?? []).map((ticket) => [ticket.ticket_number, ticket])),
    cases: new Map((refs.data?.cases ?? []).map((kase) => [kase.case_number, kase])),
  }
  return (
    <div className={className ?? 'min-w-0 max-w-full space-y-2 [overflow-wrap:anywhere]'} data-slot="riley-markdown">
      {refs.isError ? (
        <ErrorBanner
          tone="warning"
          title="Ticket and case links are unreachable"
          detail={`${describeError(refs.error)} The references remain visible without linked case details.`}
          onRetry={() => void refs.refetch()}
        />
      ) : null}
      {tree.children.map((node, index) => renderMarkdownNode(node, maps, `root.${index}`))}
    </div>
  )
}
